import { useEffect, useMemo, useState } from "react";
import { filter, orderBy } from "lodash";
import AutomationService, { Automation } from "@/app/_services/AutomationService";
import { toast } from "@/app/_utils/toast";

export const ALL = "all";

export function useAutomationsModel() {
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [eventFilter, setEventFilter] = useState<string>(ALL);

  async function loadAutomations() {
    setIsLoading(true);
    try {
      const data = await AutomationService.getAutomationsAsync();
      setAutomations(orderBy(data, ["priority", "name"], ["asc", "asc"]));
    } catch (error) {
      console.error(error);
      toast.error("Could not load automations");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadAutomations();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return filter(automations, (a) => {
      if (statusFilter !== ALL && a.status !== statusFilter) return false;
      if (eventFilter !== ALL && a.event_key !== eventFilter) return false;
      if (!term) return true;
      return (
        a.name.toLowerCase().includes(term) ||
        a.slug.toLowerCase().includes(term) ||
        a.event_key.toLowerCase().includes(term)
      );
    });
  }, [automations, search, statusFilter, eventFilter]);

  /** Events actually in use, so the filter only offers ones that would match something. */
  const usedEventKeys = useMemo(
    () => Array.from(new Set(automations.map((a) => a.event_key))).sort(),
    [automations]
  );

  async function handleDelete(automation: Automation) {
    try {
      const res = await AutomationService.deleteAutomationAsync(automation.id);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail ?? "Could not delete this automation");
        return;
      }
      // 204 is a real delete; 200 means the server archived it instead because it has
      // run history. Say which happened rather than claiming a delete that did not.
      if (res.status === 204) {
        toast.success(`${automation.name} deleted`);
      } else {
        toast.success(`${automation.name} archived — it has run history`);
      }
      loadAutomations();
    } catch (error) {
      console.error(error);
      toast.error("Could not delete this automation");
    }
  }

  return {
    automations: filtered,
    totalCount: automations.length,
    isLoading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    eventFilter,
    setEventFilter,
    usedEventKeys,
    refetch: loadAutomations,
    handleDelete,
  };
}
